<script setup lang="ts">
import { ref, computed, onBeforeUnmount, onDeactivated, watch, nextTick } from "vue";
import SvgIcon from "./SvgIcon.vue";

const props = defineProps({
  position: {
    type: String,
    default: "top-mid",
  },
  svgHeight: {
    type: Number,
    default: 14,
  },
  svgWidth: {
    type: Number,
    default: 14,
  },
  parentRef: {
    type: Object,
    default: {},
  },
  appendToBody: {
    type: Boolean,
    default: false,
  },
  forcePositionLeft: {
    type: Number,
  },
  forcePositionTop: {
    type: Number,
  },
  minified: {
    type: Boolean,
    default: false,
  },
  light: {
    type: Boolean,
    default: false,
  },
  // When true the tooltip never opens (used to keep a stable trigger element while
  // toggling the tooltip on/off, e.g. only show it when text is truncated).
  disabled: {
    type: Boolean,
    default: false,
  },
  // Also open while focus is inside the trigger, and close on Escape — hover alone leaves a
  // focusable trigger (e.g. truncated text) out of reach for keyboard users.
  openOnFocus: {
    type: Boolean,
    default: false,
  },
});

const parent = ref<any>(null);
const tooltip = ref<any>(null);
const isHovered = ref<boolean>(false);
const isFocused = ref<boolean>(false);
const showTooltip = computed<boolean>(
  () => !props.disabled && (isHovered.value || (props.openOnFocus && isFocused.value)),
);
const tooltipStyle = ref<any>({});

const positionClass = computed(() => {
  switch (props.position) {
    case "top-right":
    case "bot-right":
      return "top-0 left-0";
    case "top-left":
    case "bot-left":
      return "top-0 right-0";
    case "mid-right":
      return "transform -translate-y-2/3 left-4";
    case "mid-left":
      return "transform -translate-y-2/3 right-4";
    case "top-mid":
    case "bot-mid":
      return "top-0 left-1/2 transform -translate-x-1/2";
    default:
      return "";
  }
});

// A teleported tooltip doesn't follow its anchor when the anchor moves under a still mouse (inner scroll,
// layout shift), so it is realigned every frame while open. An inline one follows its anchor by itself.
let syncFrame: number | null = null;

function startPositionSync() {
  stopPositionSync();
  const step = () => {
    // Closed again before this frame (very brief hover).
    if (!showTooltip.value) {
      syncFrame = null;
      return;
    }
    calculateTooltipPosition();
    syncFrame = requestAnimationFrame(step);
  };
  step();
}

function stopPositionSync() {
  if (syncFrame !== null) cancelAnimationFrame(syncFrame);
  syncFrame = null;
}

watch(showTooltip, (visible) => {
  if (!visible) {
    stopPositionSync();
    return;
  }
  // The tooltip has only just been rendered or shown: measure it once the DOM is updated.
  nextTick(props.appendToBody ? startPositionSync : calculateTooltipPosition);
});

onBeforeUnmount(stopPositionSync);
// A <KeepAlive> page is deactivated, not unmounted.
onDeactivated(stopPositionSync);

function applyStyle(style: Record<string, string>) {
  const current = tooltipStyle.value;
  if (current.top === style.top && current.left === style.left) return;
  tooltipStyle.value = style;
}

// Escape closes it without moving focus away; the next hover or focus reopens it.
function dismiss() {
  if (!props.openOnFocus) return;
  isHovered.value = false;
  isFocused.value = false;
}

function calculateTooltipPosition() {
  const parentRect = !Object.keys(props.parentRef as object).length
    ? parent.value?.getBoundingClientRect()
    : props.parentRef?.getBoundingClientRect();
  const tooltipRect = tooltip.value?.getBoundingClientRect();

  if (!parentRect || !tooltipRect) {
    // Les dimensions ne sont pas disponibles, sortir de la fonction
    return;
  } else if (parentRect && tooltipRect) {
    if (props.appendToBody && props.position === "mid-right") {
      // Beside its trigger, vertically centred; forcePositionLeft is the gap between the two.
      applyStyle({
        left: `${parentRect.right + (props.forcePositionLeft ?? 0) + window.scrollX}px`,
        top: `${parentRect.top + (parentRect.height - tooltipRect.height) / 2 + window.scrollY}px`,
      });
    } else if (props.appendToBody) {
      // Teleported into <body>, the tooltip is placed in document coordinates, but getBoundingClientRect()
      // is in viewport ones: clamp it on screen in the viewport, then add the document scroll.
      const parentCenterX = parentRect.left + parentRect.width / 2;
      const tooltipLeft = parentCenterX - tooltipRect.width / 2;

      const margin = 16;
      const rawLeft = props.forcePositionLeft ? tooltipLeft - props.forcePositionLeft : tooltipLeft;
      const maxLeft = window.innerWidth - tooltipRect.width - margin;
      const clampedLeft = Math.max(margin, Math.min(rawLeft, maxLeft));

      // Above by default; below only when it would overflow the top and there is more room below.
      const gap = props.forcePositionTop ?? 0;
      const above = parentRect.top - tooltipRect.height - gap;
      const below = parentRect.bottom + gap;
      const spaceBelow = window.innerHeight - parentRect.bottom;
      const top = above < margin && spaceBelow > parentRect.top ? below : above;

      applyStyle({
        left: `${clampedLeft + window.scrollX}px`,
        top: `${top + window.scrollY}px`,
      });
    } else {
      const top = ["top-right", "top-left", "top-mid"].includes(props.position)
        ? -tooltipRect?.height
        : parentRect.height;

      applyStyle({
        top: `${top}px`,
      });
    }
  }
}
</script>
<template>
  <div
    ref="parent"
    class="relative flex"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
    @focusin="isFocused = true"
    @focusout="isFocused = false"
    @keydown.esc="dismiss"
  >
    <slot name="img">
      <SvgIcon
        class="text-secondary-color"
        name="tooltip"
        :style="{ height: svgWidth + 'px', width: svgWidth + 'px' }"
      ></SvgIcon>
    </slot>
    <Teleport v-if="appendToBody" to="body">
      <div
        v-show="showTooltip"
        ref="tooltip"
        class="tooltip__container"
        :class="[minified ? 'py-[4px] px-[12px]' : 'py-[12px] px-[16px]', light && 'tooltip__container--light']"
        :style="tooltipStyle"
      >
        <slot name="content">default tooltip text!</slot>
      </div>
    </Teleport>
    <div v-else-if="showTooltip">
      <div
        ref="tooltip"
        class="tooltip__container"
        :class="[
          positionClass,
          minified ? 'py-[4px] px-[12px]' : 'py-[12px] px-[16px]',
          light && 'tooltip__container--light',
        ]"
        :style="tooltipStyle"
      >
        <slot name="content">default tooltip text!</slot>
      </div>
    </div>
  </div>
</template>

<style scoped>
.tooltip__container {
  @apply rounded-lg absolute;
  z-index: 501;
  background-color: var(--black-dark);
  color: var(--white-main);
}

.tooltip__container.tooltip__container--light {
  background-color: var(--white-main);
  color: var(--text-main-color);
  border: 1px solid var(--grey-lightest);
  box-shadow: var(--shadow-hard);
}
</style>
